/**
 * Resolves tracking consent from explicit stored preference first,
 * then region default.
 *
 * EU/EEA + UK + Switzerland require explicit consent.
 * All other regions default to granted unless the user explicitly opted out.
 */

const CONSENT_REQUIRED_COUNTRIES = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE",
  "IS", "LI", "NO",
  "GB",
  "CH",
]);

export const COOKIE_CONSENT_STORAGE_KEY = "cookieConsent";
export const CONSENT_COUNTRY_COOKIE_NAME = "rp_consent_country";
const COOKIE_CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

export type ConsentDefault = "granted" | "denied";

export type ConsentState = {
  adConsent: boolean;
  analyticsConsent: boolean;
};

export function getConsentDefault(countryCode: string | null): ConsentDefault {
  if (!countryCode) return "denied";
  return CONSENT_REQUIRED_COUNTRIES.has(countryCode.toUpperCase())
    ? "denied"
    : "granted";
}

export function getBrowserConsentCountryCode(): string | null {
  return getBrowserCookieValue(CONSENT_COUNTRY_COOKIE_NAME);
}

export function getBrowserCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(prefix));

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
}

export function consentStateFromDefault(
  consentDefault: ConsentDefault,
): ConsentState {
  const granted = consentDefault === "granted";
  return {
    adConsent: granted,
    analyticsConsent: granted,
  };
}

export function parseStoredConsent(
  consentValue?: string | null,
): ConsentState | null {
  if (!consentValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(consentValue) as {
      adConsent?: unknown;
      analyticsConsent?: unknown;
    };

    if (
      typeof parsed.adConsent !== "boolean" ||
      typeof parsed.analyticsConsent !== "boolean"
    ) {
      return null;
    }

    return {
      adConsent: parsed.adConsent,
      analyticsConsent: parsed.analyticsConsent,
    };
  } catch {
    return null;
  }
}

export function serializeConsent(consent: ConsentState): string {
  return JSON.stringify(consent);
}

export function resolveConsent(params: {
  countryCode: string | null;
  consentValue?: string | null;
}): ConsentState {
  const storedConsent = parseStoredConsent(params.consentValue);
  if (storedConsent) {
    return storedConsent;
  }

  return consentStateFromDefault(getConsentDefault(params.countryCode));
}

export function getBrowserConsentState(): ConsentState {
  if (typeof window === "undefined") {
    return consentStateFromDefault("denied");
  }

  return (
    parseStoredConsent(window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY)) ??
    parseStoredConsent(getBrowserCookieValue(COOKIE_CONSENT_STORAGE_KEY)) ??
    consentStateFromDefault(getConsentDefault(getBrowserConsentCountryCode()))
  );
}

export function hasBrowserAnalyticsConsent(): boolean {
  return getBrowserConsentState().analyticsConsent;
}

export function shouldShowConsentBanner(params: {
  countryCode: string | null;
  consentValue?: string | null;
}): boolean {
  return (
    parseStoredConsent(params.consentValue) === null &&
    getConsentDefault(params.countryCode) === "denied"
  );
}

export function persistConsent(
  consent: ConsentState,
  options?: { notify?: boolean },
): void {
  if (typeof window === "undefined") {
    return;
  }

  const value = serializeConsent(consent);
  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, value);
  const secure = window.location.protocol === "https:" ? ";Secure" : "";
  document.cookie = `${COOKIE_CONSENT_STORAGE_KEY}=${value};path=/;max-age=${COOKIE_CONSENT_MAX_AGE_SECONDS};SameSite=Lax${secure}`;

  if (options?.notify === false) {
    return;
  }

  window.dispatchEvent(
    new StorageEvent("storage", {
      key: COOKIE_CONSENT_STORAGE_KEY,
      newValue: value,
    }),
  );
}
